import { useEffect, useState, type FC } from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableShimmer,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import { FilterSearchStatusSubmission } from 'app/KinerjaDinasPage/components/FilterStatusSubmission'
import usePerencanaanDinas from 'app/KinerjaDinasPage/hooks/usePerencanaanDinas'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import useTabKsDefinitifPltGuru from 'app/KinerjaShared/hooks/useTabKsDefinitifPltGuru'
import { getTabDetail } from 'app/KinerjaShared/components/TabKsDefinitifPltGuru'

import type {
  FilterItem,
  AdditionalFilterItem,
} from 'app/KinerjaDinasPage/components/FilterStatusSubmission'
import type { PerencanaanKsByDinasTipe } from 'types/kinerja-ks'

const TIPE_FILTER_VALUES: PerencanaanKsByDinasTipe[] = ['SPF', 'SPNF']

const KS_DEFINITIF_ADDITIONAL_FILTERS: AdditionalFilterItem[] = [
  { value: ['SPF'], label: 'Kepala Sekolah Definitif', isTipe: true },
  { value: ['SPNF'], label: 'Kepala SKB Definitif', isTipe: true },
]

type PerencanaanPegawaiProps = {}

const PerencanaanPegawai: FC<PerencanaanPegawaiProps> = ({}) => {
  const router = useRouter()

  // To store the active filter and search params
  const [activeParams, setActiveParams] = useState<FilterItem[]>()

  const {
    render: tabs,
    activeTabIndex,
    activeTabName,
    setDefaultTab,
  } = useTabKsDefinitifPltGuru({
    onChange: (tab) => {
      const { tab: queryTab } = router.query
      const tabNo = parseInt(queryTab as string, 10)
      const resetFilter = tab.index !== tabNo

      // Reset filter to ALL when tab is changed
      if (resetFilter) setActiveParams(['ALL'])

      // Change the query params
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.set('tab', tab.index.toString())
      if (resetFilter) newUrl.searchParams.set('filter', 'ALL')
      router.push(newUrl.toString(), undefined, { shallow: true })
    },
  })

  const activeFilter = activeParams?.[0]
  const isTipeFilter = TIPE_FILTER_VALUES.includes(
    activeFilter as PerencanaanKsByDinasTipe
  )
  const activeState = isTipeFilter
    ? undefined
    : (activeFilter as Parameters<typeof usePerencanaanDinas>[0]['state'])
  const activeTipe = isTipeFilter
    ? (activeFilter as PerencanaanKsByDinasTipe)
    : undefined

  const {
    // Kepala Sekolah
    metasKs,
    refetchListKs,

    // Guru
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    hasItems,

    // Relative to active tab
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePerencanaanDinas({
    activeTabName,
    state: activeState,
    tipe: activeTipe,
    role: activeTabName === 'KS' ? 'KS_DEFINITIF' : 'KS_PLT',
  })

  useEffect(() => {
    if (router.isReady) {
      const { tab: queryTab, filter: queryFilter } = router.query
      const tabNo = parseInt(queryTab as string, 10)
      const filter = queryFilter as string

      // Set default tab based on query
      if (tabNo >= 0) setDefaultTab(getTabDetail(tabNo))
      else setDefaultTab({ name: 'KS', index: 0 })

      // Set active filter based on query
      if (
        filter &&
        ['ALL', 'READY', 'DRAFT', ...TIPE_FILTER_VALUES].includes(filter)
      )
        setActiveParams([filter as FilterItem])
      else setActiveParams(['ALL'])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  return (
    <>
      <Header
        title="Daftar Perencanaan Kinerja"
        className="mb-9 print:hidden"
      />

      <section className="mt-6 bg-white border rounded-lg border-subdued">
        {tabs}

        {/* Filter & Search */}
        <FilterSearchStatusSubmission
          key={`filter-search-${activeTabName}`}
          className="px-5 my-5"
          isLoading={isListLoading}
          data={
            activeTabName === 'GURU'
              ? {
                  ALL: metasGuru?.counts?.all,
                  READY: metasGuru?.counts?.ready,
                  DRAFT: metasGuru?.counts?.draft,
                }
              : {
                  ALL: metasKs?.counts?.all,
                  READY: metasKs?.counts?.ready,
                  DRAFT: metasKs?.counts?.draft,
                }
          }
          additionalFilters={
            activeTabName === 'KS' ? KS_DEFINITIF_ADDITIONAL_FILTERS : undefined
          }
          tipeCounts={activeTabName === 'KS' ? metasKs?.countsTipe : undefined}
          activeParams={activeParams}
          onChange={({ filter }) => {
            setActiveParams(filter)

            const newUrl = new URL(window.location.href)
            newUrl.searchParams.set('tab', activeTabIndex.toString())
            newUrl.searchParams.set('filter', filter?.[0] || 'ALL')
            router.push(newUrl.toString(), undefined, { shallow: true })
          }}
        />

        <TablePelaksanaanKinerja className="mt-0">
          <TableHeader>
            <TableHeaderItem className="w-1/5">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-1/5 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Jabatan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-1/5 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Satuan Pendidikan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-1/5 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Status
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-1/5 pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={6} />}

            {/* If error or no data returned */}
            {!isListLoading &&
              (isRelativeError || relativeItems?.length === 0) && (
                <TableRowStates
                  colSpan={6}
                  type={isRelativeError ? 'ERROR' : 'EMPTY'}
                  onClick={() => {
                    activeTabName === 'GURU'
                      ? refetchListGuru()
                      : refetchListKs()
                  }}
                >
                  Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                  <br />
                  Anda dan sudah memulai tahap Perencanaan Kinerja.
                </TableRowStates>
              )}

            {!isListLoading &&
              isRelativeSuccess &&
              hasItems &&
              relativeItems.map((item, idx) => {
                return (
                  <TableRow key={`perencanaan-${idx}`}>
                    <TableRowItem className="align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.nama}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="pl-0 align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.jabatan}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="pl-0 align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.satuanPendidikan}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="pl-0 align-middle">
                      {item?.state === 'READY' ? (
                        <Badge variant="success">Sudah dikumpulkan</Badge>
                      ) : (
                        <Badge variant="critical">Belum dikumpulkan</Badge>
                      )}
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        color="black"
                        size="sm"
                        disabled={item?.state !== 'READY'}
                        onClick={() => {
                          let newUrl = new URL(
                            `${PENGELOLAAN_KINERJA_KADIN_PATH}/perencanaan/detail`,
                            process.env.NEXT_PUBLIC_BASE_URL
                          )

                          if (activeTabName === 'GURU') {
                            newUrl = new URL(
                              `${PENGELOLAAN_KINERJA_KADIN_PATH}/perencanaan/detail/guru`,
                              process.env.NEXT_PUBLIC_BASE_URL
                            )
                            newUrl.searchParams.set('ptkId', item?.ptkId)
                          }

                          newUrl.searchParams.set('npsn', item?.npsn)

                          router.push(newUrl.toString())
                        }}
                      >
                        Cek
                      </Button>
                    </TableRowItem>
                  </TableRow>
                )
              })}
          </TableBody>
        </TablePelaksanaanKinerja>
      </section>
    </>
  )
}
export default PerencanaanPegawai
